import { defineStore } from 'pinia';
import { listHelpDocs, loadHelpDoc, searchHelpDocs, type HelpDocMeta } from '@/help/loader';
import type { ParsedHelpDoc } from '@/help/frontmatter';

interface HelpState {
    isOpen: boolean;
    routeKey: string | null;
    contextStack: string[];
    currentDoc: ParsedHelpDoc | null;
    currentKey: string | null;
    loading: boolean;
    index: HelpDocMeta[];
    indexLoaded: boolean;
    indexLoading: boolean;
    searchQuery: string;
    loadSeq: number;
}

export const useHelpStore = defineStore('help', {
    state: (): HelpState => ({
        isOpen: false,
        routeKey: null,
        contextStack: [],
        currentDoc: null,
        currentKey: null,
        loading: false,
        index: [],
        indexLoaded: false,
        indexLoading: false,
        searchQuery: '',
        loadSeq: 0,
    }),

    getters: {
        activeKey(state): string | null {
            return state.contextStack[state.contextStack.length - 1] ?? state.routeKey;
        },

        searchResults(state): HelpDocMeta[] {
            return searchHelpDocs(state.searchQuery, state.index);
        },
    },

    actions: {
        setRouteKey(key: string | null | undefined) {
            const previousActiveKey = this.activeKey;
            this.routeKey = key ?? null;

            // If the drawer is already open and the resolved active doc changed,
            // refresh it so the content tracks the current route.
            if (this.isOpen && previousActiveKey !== this.activeKey) {
                void this.loadActive();
            }
        },

        pushContext(key: string) {
            this.contextStack.push(key);
            if (this.isOpen) {
                void this.loadActive();
            }
        },

        popContext(key: string) {
            const idx = this.contextStack.lastIndexOf(key);
            if (idx !== -1) {
                this.contextStack.splice(idx, 1);
            }
            if (this.isOpen) {
                void this.loadActive();
            }
        },

        async open() {
            this.isOpen = true;
            await Promise.all([this.loadIndex(), this.loadActive()]);
        },

        close() {
            this.isOpen = false;
            this.searchQuery = '';
        },

        toggle() {
            if (this.isOpen) {
                this.close();
            } else {
                void this.open();
            }
        },

        async loadIndex() {
            if (this.indexLoaded || this.indexLoading) {
                return;
            }
            this.indexLoading = true;
            try {
                this.index = await listHelpDocs();
                this.indexLoaded = true;
            } catch (error) {
                this.index = [];
                this.indexLoaded = true;
                if (import.meta.env.DEV) {
                    console.warn('[help] failed to load index', error);
                }
            } finally {
                this.indexLoading = false;
            }
        },

        async loadActive() {
            const key = this.activeKey;
            await this.loadDoc(key);
        },

        async loadDoc(key: string | null) {
            // Bump the sequence even on the null path so any in-flight load
            // from a previous call is invalidated and can't resurface after
            // the active key has been cleared.
            this.loadSeq += 1;
            const seq = this.loadSeq;

            this.currentKey = key;
            if (!key) {
                this.currentDoc = null;
                this.loading = false;
                return;
            }

            this.loading = true;
            try {
                let doc: ParsedHelpDoc | null = null;
                try {
                    doc = await loadHelpDoc(key);
                } catch (error) {
                    if (import.meta.env.DEV) {
                        console.warn(`[help] failed to load doc for key: ${key}`, error);
                    }
                }

                if (seq !== this.loadSeq) {
                    return;
                }

                this.currentDoc = doc;

                if (!doc && import.meta.env.DEV) {
                    console.warn(`[help] missing doc for key: ${key}`);
                }
            } finally {
                if (seq === this.loadSeq) {
                    this.loading = false;
                }
            }
        },

        setSearchQuery(query: string) {
            this.searchQuery = query;
        },
    },
});
